import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  MdRefresh, MdClose, MdTimer, MdCheck, MdVolumeUp,
  MdNotifications, MdDeliveryDining, MdStorefront,
  MdAdd, MdRemove, MdDelete, MdAccessTime,
  MdPayment, MdCircle, MdSwapHoriz, MdPrint, MdComment,
} from "react-icons/md";
import { IoFastFood } from "react-icons/io5";
import { useAuth } from "contexts/AuthContext";
import {
  getOrders, updateOrderStatus, markOrderReady, cancelOrder,
  addOrderProduct, updateOrderProduct, deleteOrderProduct, getOrderDetail,
} from "services/ordersService";
import { getMenus, getCategories, getProducts, getProductOptions, setProductAvailability } from "services/menuService";
import { useNewOrderAlert } from "hooks/useNewOrderAlert";
import { printClientOrderTicket, printOrderTicket } from "utils/printReceipt";
import OperationalStatusBar from "components/OperationalStatusBar";

// ── Status registry ───────────────────────────────────────────────────────────
const STATUS = {
  1: { label: "Nouveau",        color: "bg-blue-100 text-blue-700",    dot: "bg-blue-500"   },
  2: { label: "Confirmé",       color: "bg-yellow-100 text-yellow-700", dot: "bg-yellow-500" },
  3: { label: "En préparation", color: "bg-orange-100 text-orange-700", dot: "bg-orange-500" },
  4: { label: "En route",       color: "bg-purple-100 text-purple-700", dot: "bg-purple-500" },
  5: { label: "Livré",          color: "bg-green-100 text-green-700",  dot: "bg-green-500"  },
  6: { label: "Refusé",         color: "bg-red-100 text-red-600",      dot: "bg-red-500"    },
};

const STATUS_GROUPS = [
  { label: "Nouveaux",          ids: [1],    border: "border-blue-500"   },
  { label: "En cours",          ids: [2, 3], border: "border-orange-500" },
  { label: "En route / Livrés", ids: [4, 5], border: "border-green-500"  },
  { label: "Refusés",           ids: [6],    border: "border-red-400"    },
];

const PREP_OPTIONS = [10, 15, 20, 30, 45, 60];

function fmt(ts) {
  if (!ts) return null;
  return new Date(ts).toLocaleString("fr-FR", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function fmtTnd(value) {
  return `${Number(value || 0).toFixed(3)} TND`;
}

function restaurantOrderTotal(order) {
  if (!order) return 0;
  if (order.orderCost_restaurant != null) return Number(order.orderCost_restaurant) || 0;
  if (order.orderCost != null) return Number(order.orderCost) || 0;
  if (order.total_restaurant != null) {
    return Math.max(0, (Number(order.total_restaurant) || 0) - (Number(order.deliveryCost || order.delivery_cost || 0) || 0));
  }
  return Number(order.total_price ?? order.total ?? 0) || 0;
}

function normalizeList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (data?.data?.id) return [data.data];
  if (data?.id) return [data];
  return [];
}

function localDateString(date = new Date()) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function orderReference(order) {
  return order?.ref || order?.reference || "Référence indisponible";
}

function getProductName(product) {
  return product?.name || product?.product?.name || "Produit";
}

function getProductQuantity(product) {
  return Number(product?.quantity ?? product?.pivot?.quantity ?? product?.product?.quantity ?? 1) || 1;
}

function getPivotId(product) {
  return product?.pivot?.id || product?.orderProductId || product?.id;
}

function getItemIds(product) {
  const raw = product?.items || product?.pivot?.items || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => Number(item?.id ?? item))
    .filter((id) => Number.isFinite(id));
}

function getOrderComment(order) {
  const raw =
    order?.specifications ||
    order?.comment ||
    order?.commentaire ||
    order?.order_comment ||
    order?.orderComment ||
    order?.note;

  if (typeof raw === "string") return raw.trim();
  if (Array.isArray(order?.notes)) {
    return order.notes
      .map((note) => note?.content || note?.comment || note)
      .filter(Boolean)
      .join("\n")
      .trim();
  }
  return "";
}

// ── OrderCard (kanban card) ───────────────────────────────────────────────────
function OrderCard({ order, onPrepare, onReady, onSelect, onPrint }) {
  const st       = STATUS[order.status_id] || STATUS[1];
  const total    = restaurantOrderTotal(order);
  const canAccept  = order.status_id === 1;
  const canPrepare = order.status_id === 2;
  const canReady   = order.status_id === 3 && !order.order_ready_on;
  const canPrint   = order.status_id >= 2; // accepted and beyond
  const hasComment = !!getOrderComment(order);

  // Urgency indicator — how long ago was this new order placed?
  const minutesSince = canAccept && order.created_at
    ? Math.floor((Date.now() - new Date(order.created_at)) / 60000)
    : null;
  const handleCardKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(order);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Ouvrir les details de la commande ${orderReference(order)}`}
      onKeyDown={handleCardKeyDown}
      className={`cursor-pointer rounded-xl border bg-white p-4 shadow-sm transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:bg-navy-800 dark:focus:ring-offset-navy-900 ${
        canAccept ? "border-blue-200 dark:border-blue-900/50" : "border-gray-100 dark:border-navy-700"
      }`}
      onClick={() => onSelect(order)}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-600 dark:text-gray-300">#{orderReference(order)}</span>
            {order.orderType === "takeaway" && (
              <span className="rounded-full bg-amber-100 text-amber-700 text-xs font-bold px-2 py-1">
                À emporter
              </span>
            )}
          </div>
          <p className="font-bold text-navy-700 dark:text-white text-base mt-1 leading-snug">
            {order.user?.first_name
              ? `${order.user.first_name} ${order.user.last_name || ""}`.trim()
              : order.user?.name || "Client"}
          </p>
          {minutesSince !== null && minutesSince >= 5 && (
            <p className={`text-sm font-bold mt-1 ${minutesSince >= 15 ? "text-red-600" : "text-orange-600"}`}>
              ⚡ Attend depuis {minutesSince} min
            </p>
          )}
        </div>
        <span className={`rounded-full px-3 py-1 text-sm font-semibold ${st.color}`}>
          {st.label}
        </span>
      </div>

      <div className="mb-3 text-sm leading-relaxed text-gray-700 dark:text-gray-200 space-y-1">
        {(order.products || []).slice(0, 2).map((p, i) => (
          <p key={i} className="flex items-start justify-between gap-2">
            <span className="min-w-0 break-words leading-snug">• {getProductName(p)}</span>
            <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-bold text-navy-700 dark:bg-navy-700 dark:text-white">
              Qté {getProductQuantity(p)}
            </span>
          </p>
        ))}
        {(order.products || []).length > 2 && (
          <p className="font-semibold text-brand-600 dark:text-brand-300">+{order.products.length - 2} autres</p>
        )}
        {hasComment && (
          <p className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold uppercase text-amber-800 dark:border-amber-500/40 dark:bg-amber-900/20 dark:text-amber-200">
            <MdComment className="h-3.5 w-3.5" />
            Commentaire client
          </p>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="font-bold text-navy-700 dark:text-white text-base">
          {Number(total).toFixed(3)} TND
        </span>
        {canAccept ? (
          <span className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-bold text-blue-700 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-200">
            Ouvrir les details
          </span>
        ) : (
          <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
          {canPrepare && (
            <button
              type="button"
              className="flex min-h-[44px] items-center gap-1 rounded-lg bg-orange-500 px-3 py-2 text-sm font-bold text-white hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 dark:focus:ring-offset-navy-800"
              onClick={() => onPrepare(order.id)}
            >
              <MdStorefront className="h-3.5 w-3.5" />
              En préparation
            </button>
          )}
          {canReady && (
            <button
              type="button"
              className="flex min-h-[44px] items-center gap-1 rounded-lg bg-green-500 px-3 py-2 text-sm font-bold text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-navy-800"
              onClick={() => onReady(order.id)}
            >
              <MdCheck className="h-3.5 w-3.5" />
              Prete
            </button>
          )}
          {canPrint && (
            <button
              type="button"
              title="Imprimer le ticket"
              aria-label={`Imprimer le ticket de la commande ${orderReference(order)}`}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:focus:ring-offset-navy-800"
              onClick={() => onPrint(order)}
            >
              <MdPrint className="h-4 w-4" />
            </button>
          )}
          </div>
        )}
      </div>
      {order.order_ready_on && (
        <p className="mt-3 flex items-center gap-1 text-sm font-semibold text-green-700 dark:text-green-300">
          <MdCheck className="h-3.5 w-3.5" />
          Commande prete a {fmt(order.order_ready_on)}
        </p>
      )}
    </div>
  );
}

// ── Accept modal ──────────────────────────────────────────────────────────────
function AcceptModal({ order, onConfirm, onClose }) {
  const [prepTime, setPrepTime] = useState(20);
  if (!order) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="accept-order-title">
      <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-navy-800 shadow-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 id="accept-order-title" className="font-bold text-xl text-navy-700 dark:text-white">Accepter la commande</h3>
          <button
            type="button"
            aria-label="Fermer la confirmation"
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] rounded-full p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:hover:bg-navy-700 dark:focus:ring-offset-navy-800"
          >
            <MdClose className="h-5 w-5 text-gray-400" />
          </button>
        </div>
        <p className="text-base text-gray-700 dark:text-gray-300 mb-4">
          Commande <span className="font-bold text-navy-700 dark:text-white">#{orderReference(order)}</span>
          {" — "}{restaurantOrderTotal(order).toFixed(3)} TND
        </p>
        <p className="text-sm font-bold text-gray-600 dark:text-gray-300 uppercase mb-2 flex items-center gap-1.5">
          <MdTimer className="h-4 w-4" /> Temps de préparation estimé
        </p>
        <div className="flex flex-wrap gap-2 mb-5">
          {PREP_OPTIONS.map((m) => (
            <button
              type="button"
              key={m}
              className={`min-h-[44px] rounded-xl px-4 py-2 text-base font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:focus:ring-offset-navy-800 ${
                prepTime === m
                  ? "bg-brand-500 text-white"
                  : "bg-gray-100 text-gray-700 dark:bg-navy-700 dark:text-white"
              }`}
              onClick={() => setPrepTime(m)}
            >
              {m} min
            </button>
          ))}
        </div>
        <button
          type="button"
          className="min-h-[48px] w-full rounded-xl bg-green-500 py-3 text-base font-bold text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-navy-800"
          onClick={() => onConfirm(order.id, prepTime)}
        >
          <MdCheck className="inline h-4 w-4 mr-1.5" />
          Confirmer — {prepTime} min
        </button>
      </div>
    </div>
  );
}

// ── Refuse modal ──────────────────────────────────────────────────────────────
function RefuseModal({ order, onConfirm, onClose }) {
  const [motif, setMotif] = useState("");
  const MOTIFS = ["Rupture de stock", "Fermeture exceptionnelle", "Zone non couverte", "Commande incomplète", "Autre"];
  if (!order) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="refuse-order-title">
      <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-navy-800 shadow-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 id="refuse-order-title" className="font-bold text-xl text-navy-700 dark:text-white">Refuser la commande</h3>
          <button
            type="button"
            aria-label="Fermer le refus de commande"
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] rounded-full p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:hover:bg-navy-700 dark:focus:ring-offset-navy-800"
          >
            <MdClose className="h-5 w-5 text-gray-400" />
          </button>
        </div>
        <p className="text-sm font-bold text-gray-600 dark:text-gray-300 uppercase mb-2">Motif de refus *</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {MOTIFS.map((m) => (
            <button
              key={m}
              type="button"
              className={`min-h-[44px] rounded-xl px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-navy-800 ${
                motif === m ? "bg-red-500 text-white" : "bg-gray-100 text-gray-700 dark:bg-navy-700 dark:text-white"
              }`}
              onClick={() => setMotif(m)}
            >
              {m}
            </button>
          ))}
        </div>
        <textarea
          aria-label="Motif de refus"
          className="w-full rounded-xl border border-gray-300 bg-gray-50 p-3 text-base text-navy-700 resize-none mb-4 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:ring-offset-navy-800"
          placeholder="Précisez le motif..."
          rows={2}
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
        />
        <div className="flex gap-2">
          <button
            type="button"
            className="min-h-[48px] flex-1 rounded-xl bg-red-500 py-3 text-base font-bold text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 dark:focus:ring-offset-navy-800"
            disabled={!motif.trim()}
            onClick={() => onConfirm(order.id, motif.trim())}
          >
            Confirmer le refus
          </button>
          <button
            type="button"
            className="min-h-[48px] rounded-xl border border-gray-300 px-4 py-3 text-base font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:border-navy-600 dark:text-gray-200 dark:hover:bg-navy-700 dark:focus:ring-offset-navy-800"
            onClick={onClose}
          >
            Retour
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductPickerModal({ visible, restaurantId, replaceTarget, onClose, onConfirm, saving }) {
  const [step, setStep] = useState("browse");
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [categoryId, setCategoryId] = useState(null);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [specifications, setSpecifications] = useState("");

  useEffect(() => {
    if (!visible) {
      setStep("browse");
      setSearch("");
      setSelectedProduct(null);
      setSelectedItems({});
      setQuantity(1);
      setSpecifications("");
      return;
    }
    if (!restaurantId) return;

    setLoadingMenu(true);
    getMenus(restaurantId)
      .then(async (menuData) => {
        const menu = normalizeList(menuData)[0];
        if (!menu?.id) {
          setCategories([]);
          setProductsByCategory({});
          setCategoryId(null);
          return;
        }

        const cats = normalizeList(await getCategories(menu.id));
        setCategories(cats);
        setCategoryId(cats[0]?.id || null);

        const nextProducts = {};
        await Promise.all(cats.map(async (cat) => {
          try {
            nextProducts[cat.id] = normalizeList(await getProducts(cat.id));
          } catch {
            nextProducts[cat.id] = [];
          }
        }));
        setProductsByCategory(nextProducts);
      })
      .catch(() => {
        setCategories([]);
        setProductsByCategory({});
      })
      .finally(() => setLoadingMenu(false));
  }, [visible, restaurantId]);

  if (!visible) return null;

  const allProducts = Object.values(productsByCategory).flat();
  const query = search.trim().toLowerCase();
  const displayedProducts = query
    ? allProducts.filter((product) => product.name?.toLowerCase().includes(query))
    : productsByCategory[categoryId] || [];
  const availableProducts = displayedProducts.filter((product) => product.available !== false && product.available !== 0);

  const itemKey = (optionId, itemId) => `${optionId}_${itemId}`;
  const getOptionItems = (option) => Array.isArray(option?.items) ? option.items : [];

  const toggleItem = (option, item) => {
    const key = itemKey(option.id, item.id);
    setSelectedItems((prev) => {
      const next = { ...prev };
      if (option.is_size || option.max === 1) {
        getOptionItems(option).forEach((choice) => {
          delete next[itemKey(option.id, choice.id)];
        });
        if (!prev[key]) next[key] = true;
      } else if (next[key]) {
        delete next[key];
      } else {
        const selectedCount = getOptionItems(option).filter((choice) => next[itemKey(option.id, choice.id)]).length;
        if (option.max > 0 && selectedCount >= option.max) return prev;
        next[key] = true;
      }
      return next;
    });
  };

  const handleSelectProduct = async (product) => {
    let next = { ...product };
    if (!Array.isArray(next.options) || next.options.length === 0) {
      try {
        next.options = normalizeList(await getProductOptions(product.id));
      } catch {
        next.options = [];
      }
    }
    setSelectedProduct(next);
    setSelectedItems({});
    setQuantity(1);
    setSpecifications("");
    setStep("configure");
  };

  const calcTotal = () => {
    if (!selectedProduct) return 0;
    let unitPrice = Number(selectedProduct.price) || 0;
    (selectedProduct.options || []).forEach((option) => {
      getOptionItems(option).forEach((item) => {
        if (selectedItems[itemKey(option.id, item.id)]) {
          unitPrice += Number(item.price) || 0;
        }
      });
    });
    return unitPrice * quantity;
  };

  const isValid = () => {
    if (!selectedProduct) return false;
    return (selectedProduct.options || []).every((option) => {
      if (!option.min) return true;
      const selectedCount = getOptionItems(option).filter((item) => selectedItems[itemKey(option.id, item.id)]).length;
      return selectedCount >= Number(option.min);
    });
  };

  const handleConfirm = () => {
    const items = Object.keys(selectedItems)
      .filter((key) => selectedItems[key])
      .map((key) => Number(key.split("_")[1]))
      .filter((id) => Number.isFinite(id));

    onConfirm(selectedProduct.id, {
      quantity,
      items,
      specifications,
      total: calcTotal(),
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-navy-800">
        <div className="flex items-center justify-between border-b border-gray-100 p-4 dark:border-navy-700">
          <button
            className="rounded-xl px-3 py-2 text-sm font-semibold text-brand-500 hover:bg-brand-50 dark:hover:bg-navy-700"
            onClick={step === "configure" ? () => setStep("browse") : onClose}
          >
            {step === "configure" ? "Retour" : "Fermer"}
          </button>
          <h3 className="min-w-0 flex-1 px-3 text-center text-base font-bold leading-snug text-navy-700 dark:text-white">
            {replaceTarget ? `Remplacer ${replaceTarget.name}` : "Ajouter un article"}
          </h3>
          <div className="w-20" />
        </div>

        {step === "browse" ? (
          <>
            <div className="border-b border-gray-100 p-4 dark:border-navy-700">
              <input
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-navy-700 outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                placeholder="Rechercher un produit..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            {!query && categories.length > 0 && (
              <div className="flex gap-2 overflow-x-auto border-b border-gray-100 px-4 py-3 dark:border-navy-700">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${
                      categoryId === cat.id
                        ? "bg-brand-500 text-white"
                        : "bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-white"
                    }`}
                    onClick={() => setCategoryId(cat.id)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
            <div className="flex-1 overflow-y-auto">
              {loadingMenu ? (
                <div className="flex justify-center py-16">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
                </div>
              ) : availableProducts.length === 0 ? (
                <p className="py-12 text-center text-sm text-gray-400">Aucun produit disponible</p>
              ) : (
                availableProducts.map((product) => (
                  <button
                    key={product.id}
                    className="flex w-full items-center justify-between gap-3 border-b border-gray-50 px-5 py-3 text-left hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-700"
                    onClick={() => handleSelectProduct(product)}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-sm font-semibold leading-snug text-navy-700 dark:text-white">{product.name}</p>
                      {product.description && (
                        <p className="mt-1 break-words text-xs leading-relaxed text-gray-500 dark:text-gray-400">{product.description}</p>
                      )}
                    </div>
                    <span className="shrink-0 text-sm font-bold text-brand-500">{Number(product.price || 0).toFixed(3)} TND</span>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5">
              <div className="mb-5">
                <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedProduct?.name}</p>
                <p className="text-sm font-semibold text-brand-500">{Number(selectedProduct?.price || 0).toFixed(3)} TND</p>
              </div>

              {(selectedProduct?.options || []).map((option) => {
                const items = getOptionItems(option);
                if (!items.length) return null;
                return (
                  <div key={option.id} className="mb-5 rounded-xl border border-gray-100 p-4 dark:border-navy-700">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <p className="font-bold text-navy-700 dark:text-white">{option.name}</p>
                      {option.min > 0 && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-bold text-orange-600">Obligatoire</span>}
                    </div>
                    <div className="space-y-2">
                      {items.map((item) => {
                        const checked = !!selectedItems[itemKey(option.id, item.id)];
                        return (
                          <button
                            key={item.id}
                            className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm ${
                              checked
                                ? "border-brand-500 bg-brand-50 text-brand-600 dark:bg-brand-900/20"
                                : "border-gray-100 text-gray-600 dark:border-navy-700 dark:text-white"
                            }`}
                            onClick={() => toggleItem(option, item)}
                          >
                            <span>{item.name}</span>
                            <span>{Number(item.price || 0) > 0 ? `+${Number(item.price).toFixed(3)} TND` : ""}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <textarea
                className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-navy-700 outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                rows={3}
                placeholder="Instructions speciales..."
                value={specifications}
                onChange={(event) => setSpecifications(event.target.value)}
              />
            </div>
            <div className="flex items-center gap-3 border-t border-gray-100 p-4 dark:border-navy-700">
              <div className="flex items-center gap-2 rounded-xl bg-gray-100 p-1 dark:bg-navy-700">
                <button className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-gray-600 dark:bg-navy-800 dark:text-white" onClick={() => setQuantity((value) => Math.max(1, value - 1))}>
                  <MdRemove />
                </button>
                <span className="w-8 text-center text-sm font-bold text-navy-700 dark:text-white">{quantity}</span>
                <button className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-gray-600 dark:bg-navy-800 dark:text-white" onClick={() => setQuantity((value) => value + 1)}>
                  <MdAdd />
                </button>
              </div>
              <button
                className="flex-1 rounded-xl bg-brand-500 py-3 text-sm font-bold text-white hover:bg-brand-600 disabled:opacity-50"
                disabled={!isValid() || saving}
                onClick={handleConfirm}
              >
                {saving ? "Enregistrement..." : `${replaceTarget ? "Remplacer" : "Ajouter"} - ${calcTotal().toFixed(3)} TND`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Order detail modal — full featured ───────────────────────────────────────
function OrderDetailModal({ orderId, restaurantId, restaurant, onClose, onAccept, onRefuse, onPrepare, onReady, onOrderUpdated }) {
  const [order,     setOrder]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(null); // orderProductId being saved
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [pickerSaving, setPickerSaving] = useState(false);
  const [outOfStock, setOutOfStock] = useState({});
  const [minOrderError, setMinOrderError] = useState(null);

  // ── Minimum-order guard ────────────────────────────────────────────────────
  // The restaurant minimum is compared against the food subtotal (orderCost),
  // never the grand total which also carries the delivery fee.
  const currentOrderCost = () => Number(order?.orderCost_restaurant || order?.orderCost) || 0;

  const lineTotalOf = (p) => {
    const qty  = p.quantity || p.pivot?.quantity || 1;
    const unit = p.price || p.base_price || 0;
    return Number(p.total_price || p.pivot?.total || (unit * qty) || 0);
  };

  const unitPriceOf = (p) => {
    const qty = p.quantity || p.pivot?.quantity || 1;
    return qty > 0 ? lineTotalOf(p) / qty : 0;
  };

  // Returns true (and surfaces a warning) when the projected food subtotal would
  // fall below the restaurant minimum — blocking the edit before it is sent.
  const blocksMinOrder = (projectedCost) => {
    const minOrder = Number(restaurant?.min_order) || 0;
    if (minOrder > 0 && projectedCost < minOrder) {
      setMinOrderError(
        `Commande minimum de ${fmtTnd(minOrder)} non atteinte — le total des produits serait de ${fmtTnd(projectedCost)}.`
      );
      return true;
    }
    return false;
  };

  const handleOutOfStock = async (productId) => {
    if (!productId || outOfStock[productId]) return;
    try {
      await setProductAvailability(productId, false);
      setOutOfStock((prev) => ({ ...prev, [productId]: true }));
    } catch {}
  };

  const reloadOrder = useCallback(async () => {
    if (!orderId) return null;
    const data = await getOrderDetail(orderId);
    const nextOrder = data.data || data;
    setOrder(nextOrder);
    return nextOrder;
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    reloadOrder()
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orderId, reloadOrder]);

  if (!orderId) return null;

  const st         = order ? (STATUS[order.status_id] || STATUS[1]) : null;
  const total      = order ? restaurantOrderTotal(order) : 0;
  const canEdit    = order?.status_id === 1; // partner can edit products only before confirmation
  const canAccept  = order?.status_id === 1;
  const canPrepare = order?.status_id === 2;
  const canReady   = order?.status_id === 3 && !order?.order_ready_on;
  const orderComment = getOrderComment(order);

  const handleQtyChange = async (product, delta) => {
    const currentQty = product.quantity || product.pivot?.quantity || 1;
    const newQty     = currentQty + delta;
    const pivotId    = getPivotId(product);
    if (newQty < 1) {
      handleRemove(product);
      return;
    }
    // Only a reduction can breach the minimum.
    if (delta < 0 && blocksMinOrder(currentOrderCost() - lineTotalOf(product) + unitPriceOf(product) * newQty)) {
      return;
    }
    setMinOrderError(null);
    setSaving(pivotId);
    try {
      await updateOrderProduct(order.id, pivotId, {
        product_id: product.product_id || product.product?.id || product.id,
        quantity: newQty,
        items: getItemIds(product),
        specifications: product.pivot?.specifications || product.specifications || "",
      });
      await reloadOrder();
      onOrderUpdated?.(order.id);
    } catch {}
    setSaving(null);
  };

  const handleRemove = async (product) => {
    const pivotId = getPivotId(product);
    if (blocksMinOrder(currentOrderCost() - lineTotalOf(product))) {
      return;
    }
    setMinOrderError(null);
    setSaving(pivotId);
    try {
      await deleteOrderProduct(order.id, pivotId);
      await reloadOrder();
      onOrderUpdated?.(order.id);
    } catch {}
    setSaving(null);
  };

  const handleOpenAddProduct = () => {
    setReplaceTarget(null);
    setShowProductPicker(true);
  };

  const handleReplaceProduct = (product) => {
    setReplaceTarget({
      orderProductId: getPivotId(product),
      name: getProductName(product),
      total: lineTotalOf(product),
    });
    setShowProductPicker(true);
  };

  const handleProductPicked = async (productId, payload) => {
    const projected = replaceTarget
      ? currentOrderCost() - (replaceTarget.total || 0) + (payload.total || 0)
      : currentOrderCost() + (payload.total || 0);
    if (blocksMinOrder(projected)) {
      // Close the picker so the warning banner behind it becomes visible.
      setShowProductPicker(false);
      setReplaceTarget(null);
      return;
    }
    setMinOrderError(null);
    setPickerSaving(true);
    try {
      if (replaceTarget?.orderProductId) {
        await deleteOrderProduct(order.id, replaceTarget.orderProductId);
      }
      await addOrderProduct(order.id, productId, payload);
      await reloadOrder();
      onOrderUpdated?.(order.id);
      setShowProductPicker(false);
      setReplaceTarget(null);
    } catch {
    } finally {
      setPickerSaving(false);
    }
  };

  const handleReady = async () => {
    if (!order?.id) return;
    try {
      await onReady(order.id);
      await reloadOrder();
      onOrderUpdated?.(order.id);
    } catch {}
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-detail-title"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-navy-800 shadow-xl overflow-y-auto max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-navy-700 sticky top-0 bg-white dark:bg-navy-800 z-10">
          {loading ? (
            <div className="h-6 w-48 bg-gray-100 dark:bg-navy-700 rounded animate-pulse" />
          ) : (
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 id="order-detail-title" className="font-bold text-xl text-navy-700 dark:text-white">
                  #{orderReference(order)}
                </h3>
                {order?.orderType === "takeaway" && (
                  <span className="rounded-full bg-amber-100 text-amber-600 text-xs font-bold px-2 py-0.5">
                    À emporter
                  </span>
                )}
                {st && (
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${st.color}`}>
                    {st.label}
                  </span>
                )}
              </div>
              {order?.created_at && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Reçue le {fmt(order.created_at)}</p>
              )}
            </div>
          )}
          <button
            type="button"
            aria-label="Fermer les details de la commande"
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] rounded-full p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:hover:bg-navy-700 dark:focus:ring-offset-navy-800 flex-shrink-0"
          >
            <MdClose className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
          </div>
        ) : !order ? (
          <div className="flex justify-center py-12 text-gray-400 text-sm">Commande introuvable</div>
        ) : (
          <div className="p-5 space-y-5 flex-1">
            {/* Client + Payment */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className="text-sm font-bold text-gray-600 dark:text-gray-300 uppercase mb-1">Client</p>
                <p className="text-base font-semibold text-navy-700 dark:text-white">
                  {order.user?.first_name
                    ? `${order.user.first_name} ${order.user.last_name || ""}`.trim()
                    : order.user?.name || "—"}
                </p>
                {order.user?.phone && (
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{order.user.phone}</p>
                )}
              </div>
              <div>
                <p className="text-sm font-bold text-gray-600 dark:text-gray-300 uppercase mb-1">Paiement</p>
                <p className="text-base font-semibold text-navy-700 dark:text-white flex items-center gap-1.5">
                  <MdPayment className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                  {order.payementMode?.name || order.payement_mode_id === 1 ? "Espèces" : "En ligne"}
                </p>
              </div>
            </div>

            {orderComment && (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 px-5 py-4 shadow-sm dark:border-amber-500/50 dark:bg-amber-900/20">
                <div className="mb-3 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="flex items-center gap-2 text-base font-black uppercase tracking-wide text-amber-800 dark:text-amber-200">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500 text-white">
                      <MdComment className="h-5 w-5" />
                    </span>
                    Commentaire client
                  </p>
                  <span className="rounded-full bg-amber-200 px-3 py-1 text-xs font-black uppercase text-amber-900 dark:bg-amber-500/30 dark:text-amber-100">
                    Important
                  </span>
                </div>
                <p className="whitespace-pre-wrap break-words text-xl font-bold leading-relaxed text-amber-950 dark:text-amber-100 sm:text-lg">
                  {orderComment}
                </p>
              </div>
            )}

            {/* Products */}
            <div>
              <p className="text-sm font-bold text-gray-600 dark:text-gray-300 uppercase mb-2">
                Produits
                {canEdit && <span className="ml-2 text-blue-700 dark:text-blue-300">(modifiable avant confirmation)</span>}
              </p>
              {minOrderError && (
                <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
                  {minOrderError}
                </div>
              )}
              <div className="space-y-3">
                {(order.products || []).map((p, i) => {
                  const pivotId  = getPivotId(p);
                  const productId = p.product_id || (p.product && p.product.id) || p.id;
                  const qty      = getProductQuantity(p);
                  const unitPrice = p.price || p.base_price || 0;
                  const lineTotal = p.total_price || p.pivot?.total || (unitPrice * qty) || 0;
                  const isSaving = saving === pivotId;
                  const isOutOfStock = !!outOfStock[productId];
                  const optionList = Array.isArray(p.items)
                    ? p.items.map((item) => item?.name || item).filter(Boolean)
                    : [];
                  const optionLabels = optionList.length
                    ? optionList.join(", ")
                    : "";
                  return (
                    <div
                      key={i}
                      className="rounded-xl bg-gray-50 px-3 py-3 dark:bg-navy-700"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <p className="min-w-0 break-words text-lg font-bold leading-snug text-navy-700 dark:text-white sm:text-base sm:font-semibold">
                          {getProductName(p)}
                        </p>
                        <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">
                          <button
                            type="button"
                            onClick={() => handleOutOfStock(productId)}
                            disabled={isOutOfStock}
                            title="Marquer le produit en rupture de stock"
                            aria-label={`Marquer ${getProductName(p)} en rupture de stock`}
                            className={`min-h-[44px] flex-shrink-0 rounded-lg border px-3 py-2 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed dark:focus:ring-offset-navy-800 ${
                              isOutOfStock
                                ? "border-red-200 bg-red-50 text-red-600"
                                : "border-red-200 text-red-600 hover:bg-red-50 dark:border-navy-600 dark:text-red-300"
                            }`}
                          >
                            {isOutOfStock ? "En rupture" : "Rupture de stock"}
                          </button>
                          {canEdit ? (
                            <>
                          <button
                            type="button"
                            aria-label={`Diminuer la quantite de ${getProductName(p)}`}
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-40 dark:border-navy-600 dark:text-gray-200 dark:focus:ring-offset-navy-800"
                            onClick={() => handleQtyChange(p, -1)}
                            disabled={isSaving}
                          >
                            <MdRemove className="h-3.5 w-3.5" />
                          </button>
                          <span className="min-w-[58px] rounded-full bg-white px-2.5 py-1 text-center text-sm font-black text-navy-700 shadow-sm ring-1 ring-gray-200 dark:bg-navy-800 dark:text-white dark:ring-navy-600">
                            {isSaving ? "..." : `Qté ${qty}`}
                          </span>
                          <button
                            type="button"
                            aria-label={`Augmenter la quantite de ${getProductName(p)}`}
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-40 dark:border-navy-600 dark:text-gray-200 dark:focus:ring-offset-navy-800"
                            onClick={() => handleQtyChange(p, 1)}
                            disabled={isSaving}
                          >
                            <MdAdd className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            aria-label={`Supprimer ${getProductName(p)} de la commande`}
                            className="ml-1 flex h-10 w-10 items-center justify-center rounded-full text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-40 dark:text-red-300 dark:focus:ring-offset-navy-800"
                            onClick={() => handleRemove(p)}
                            disabled={isSaving}
                          >
                            <MdDelete className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            aria-label={`Remplacer ${getProductName(p)}`}
                            className="flex h-10 w-10 items-center justify-center rounded-full text-orange-600 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-40 dark:text-orange-300 dark:focus:ring-offset-navy-800"
                            onClick={() => handleReplaceProduct(p)}
                            disabled={isSaving}
                            title="Remplacer"
                          >
                            <MdSwapHoriz className="h-3.5 w-3.5" />
                          </button>
                            </>
                          ) : (
                            <>
                              <span className="rounded-full bg-white px-2.5 py-1 text-sm font-black text-navy-700 ring-1 ring-gray-200 dark:bg-navy-800 dark:text-white dark:ring-navy-600">
                                Qté {qty}
                              </span>
                              <span className="text-base font-semibold text-navy-700 dark:text-white">
                                {Number(lineTotal).toFixed(3)} TND
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      {(p.pivot?.specifications || p.specifications) && (
                        <div className="mt-3 w-full rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 font-semibold leading-relaxed text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
                          <span className="mb-1 flex items-center gap-1.5 text-xs font-black uppercase tracking-wide text-amber-700 dark:text-amber-300">
                            <MdComment className="h-3.5 w-3.5" />
                            Instruction article
                          </span>
                          <span className="block max-h-48 overflow-y-auto whitespace-pre-wrap break-words pr-1 text-base">
                            {p.pivot?.specifications || p.specifications}
                          </span>
                        </div>
                      )}
                      {optionLabels && (
                        <div className="mt-3 rounded-lg bg-white px-3 py-2 dark:bg-navy-800">
                          <p className="mb-2 text-xs font-black uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Options
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {optionList.map((label, optionIndex) => (
                              <span
                                key={`${label}-${optionIndex}`}
                                className="rounded-full bg-gray-100 px-2.5 py-1 text-sm font-semibold text-gray-700 dark:bg-navy-700 dark:text-gray-200"
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {canEdit && (
                <button
                  type="button"
                  className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border border-dashed border-brand-500 bg-brand-50 py-3 text-base font-bold text-brand-600 hover:bg-brand-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:bg-brand-900/10 dark:text-brand-300 dark:focus:ring-offset-navy-800"
                  onClick={handleOpenAddProduct}
                >
                  <MdAdd className="h-4 w-4" />
                  Ajouter un article
                </button>
              )}
            </div>

            {/* Total */}
            <div className="flex justify-between border-t border-gray-100 dark:border-navy-700 pt-3">
              <span className="text-base font-bold text-navy-700 dark:text-white">Total</span>
              <span className="font-bold text-brand-600 dark:text-brand-300 text-xl">{Number(total).toFixed(3)} TND</span>
            </div>

            {/* Prep time */}
            {order.estimated_preparation_time > 0 && (
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                <MdTimer className="h-4 w-4" />
                Préparation estimée : {order.estimated_preparation_time} min
              </p>
            )}

            {order.order_ready_on && (
              <p className="text-sm font-semibold text-green-700 dark:text-green-300 flex items-center gap-1.5">
                <MdCheck className="h-4 w-4" />
                Commande prete depuis {fmt(order.order_ready_on)}
              </p>
            )}

            {/* Delivery info */}
            {[4, 5].includes(order.status_id) && (
              <div className="rounded-xl bg-purple-50 dark:bg-purple-900/20 px-4 py-3 flex items-center gap-2">
                <MdDeliveryDining className="h-5 w-5 text-purple-500 flex-shrink-0" />
                <div>
                  <p className="text-base font-semibold text-purple-700 dark:text-purple-300">
                    {order.status_id === 4 ? "Livreur en route" : "Commande livrée"}
                  </p>
                  {order.delivery && (
                    <p className="text-sm text-purple-700 dark:text-purple-200 mt-1">
                      {order.delivery.first_name || order.delivery.name || "Livreur assigné"}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Refusal reason */}
            {order.status_id === 6 && order.raison && (
              <div className="rounded-xl bg-red-50 dark:bg-red-900/20 px-4 py-3">
                <p className="text-sm font-bold text-red-600 dark:text-red-300 uppercase mb-1">Motif de refus</p>
                <p className="text-base font-medium text-red-700 dark:text-red-200">{order.raison}</p>
              </div>
            )}

            {/* Timeline */}
            <div>
              <p className="text-sm font-bold text-gray-600 dark:text-gray-300 uppercase mb-2 flex items-center gap-1.5">
                <MdAccessTime className="h-3.5 w-3.5" /> Historique
              </p>
              <div className="space-y-2">
                {[
                  { label: "Commande reçue",   ts: order.created_at,    color: "bg-blue-500"   },
                  { label: "Confirmée",         ts: order.confirmed_at,  color: "bg-yellow-500" },
                  { label: "Commande prete",    ts: order.order_ready_on, color: "bg-green-500"  },
                  { label: "Livreur en route",  ts: order.onroad_at,     color: "bg-purple-500" },
                  { label: "Livrée",            ts: order.delivred_at,   color: "bg-green-500"  },
                  { label: "Annulée",           ts: order.canceled_at,   color: "bg-red-500"    },
                ]
                  .filter((e) => !!e.ts)
                  .map((e, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm">
                      <MdCircle className={`h-2 w-2 flex-shrink-0 ${e.color} rounded-full`} />
                      <span className="text-gray-700 dark:text-gray-300 flex-1">{e.label}</span>
                      <span className="text-navy-700 dark:text-white font-medium">{fmt(e.ts)}</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Partner actions */}
            {canAccept && (
              <div className="flex flex-col gap-3 pt-1 sm:flex-row">
                <button
                  type="button"
                  className="min-h-[48px] flex-1 rounded-xl bg-green-500 py-3 text-base font-bold text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-navy-800"
                  onClick={() => { onClose(); onAccept(order); }}
                >
                  <MdCheck className="inline h-4 w-4 mr-1" />
                  Accepter
                </button>
                <button
                  type="button"
                  className="min-h-[48px] flex-1 rounded-xl border border-red-300 py-3 text-base font-bold text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:border-red-700 dark:text-red-300 dark:focus:ring-offset-navy-800"
                  onClick={() => { onClose(); onRefuse(order); }}
                >
                  Refuser
                </button>
              </div>
            )}
            {canPrepare && (
              <button
                type="button"
                className="min-h-[48px] w-full rounded-xl bg-orange-500 py-3 text-base font-bold text-white hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 dark:focus:ring-offset-navy-800"
                onClick={() => { onPrepare(order.id); onClose(); }}
              >
                <MdStorefront className="inline h-4 w-4 mr-1" />
                Mettre en préparation
              </button>
            )}
            {canReady && (
              <button
                type="button"
                className="min-h-[48px] w-full rounded-xl bg-green-500 py-3 text-base font-bold text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-navy-800"
                onClick={handleReady}
              >
                <MdCheck className="inline h-4 w-4 mr-1" />
                Marquer comme prete
              </button>
            )}
            {order.status_id >= 2 && (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border border-gray-300 py-3 text-base font-bold text-navy-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:border-navy-600 dark:text-white dark:hover:bg-navy-700 dark:focus:ring-offset-navy-800"
                  onClick={() => printOrderTicket(order, restaurant)}
                >
                  <MdPrint className="h-4 w-4" />
                  Ticket restaurant
                </button>
                <button
                  type="button"
                  className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border border-gray-300 py-3 text-base font-bold text-navy-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:border-navy-600 dark:text-white dark:hover:bg-navy-700 dark:focus:ring-offset-navy-800"
                  onClick={() => printClientOrderTicket(order, restaurant)}
                >
                  <MdPrint className="h-4 w-4" />
                  Ticket client
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <ProductPickerModal
        visible={showProductPicker}
        restaurantId={restaurantId}
        replaceTarget={replaceTarget}
        onClose={() => {
          setShowProductPicker(false);
          setReplaceTarget(null);
        }}
        onConfirm={handleProductPicked}
        saving={pickerSaving}
      />
    </div>
  );
}

// ── Main Orders page ──────────────────────────────────────────────────────────
export default function Orders() {
  const { restaurantId, restaurant } = useAuth();
  const [orders,      setOrders]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [detailId,    setDetailId]    = useState(null); // id of order shown in detail modal
  const [acceptOrder, setAcceptOrder] = useState(null);
  const [refuseOrder, setRefuseOrder] = useState(null);
  const [dateFilter,  setDateFilter]  = useState(() => localDateString());
  const [searchRef,   setSearchRef]   = useState("");
  const pollingRef = useRef(null);

  const { newOrderIds, acknowledge } = useNewOrderAlert(orders);

  const load = useCallback(async (silent = false) => {
    if (!restaurantId) return;
    if (!silent) setLoading(true);
    try {
      const params = dateFilter ? { orderDate: dateFilter } : {};
      const data = await getOrders(restaurantId, params);
      setOrders(Array.isArray(data) ? data : data.data || []);
    } catch {}
    finally { if (!silent) setLoading(false); }
  }, [restaurantId, dateFilter]);

  useEffect(() => {
    load();
    pollingRef.current = setInterval(() => load(true), 60000);
    return () => clearInterval(pollingRef.current);
  }, [load]);

  useEffect(() => {
    const handleNewOrder = () => load(true);
    window.addEventListener("restaurant:new-order", handleNewOrder);
    return () => window.removeEventListener("restaurant:new-order", handleNewOrder);
  }, [load]);

  const handleAccept = async (orderId, prepTimeMins) => {
    try {
      await updateOrderStatus(orderId, 2, prepTimeMins);
      setOrders((prev) => prev.map((o) =>
        o.id === orderId ? { ...o, status_id: 2, estimated_preparation_time: prepTimeMins } : o
      ));
    } catch {}
    setAcceptOrder(null);
    acknowledge();
  };

  const handleRefuse = async (orderId, motif) => {
    try {
      await cancelOrder(orderId, motif);
      setOrders((prev) => prev.map((o) =>
        o.id === orderId ? { ...o, status_id: 6, raison: motif } : o
      ));
    } catch {}
    setRefuseOrder(null);
    acknowledge();
  };

  const handlePrepare = async (orderId) => {
    try {
      await updateOrderStatus(orderId, 3);
      setOrders((prev) => prev.map((o) =>
        o.id === orderId ? { ...o, status_id: 3 } : o
      ));
    } catch {}
  };

  const handleReady = async (orderId) => {
    try {
      const data = await markOrderReady(orderId);
      const readyOrder = data?.data || data || {};
      const readyAt = readyOrder.order_ready_on || new Date().toISOString();
      setOrders((prev) => prev.map((o) =>
        o.id === orderId ? { ...o, ...readyOrder, order_ready_on: readyAt } : o
      ));
    } catch {}
  };

  // Fetch the full order (options, notes, address) then open the print dialog.
  const handlePrint = async (order) => {
    try {
      const data = await getOrderDetail(order.id);
      printOrderTicket(data.data || data, restaurant);
    } catch {
      // Fall back to the summary data already on the card.
      printOrderTicket(order, restaurant);
    }
  };

  const filtered = orders.filter((o) =>
    !searchRef || orderReference(o).toLowerCase().includes(searchRef.toLowerCase())
  );

  return (
    <div className="mt-3">
      {/* New-order alert banner */}
      {newOrderIds.length > 0 && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl bg-brand-500 px-5 py-3 shadow-lg animate-pulse">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white/20">
              <MdNotifications className="h-5 w-5 text-white" />
            </span>
            <div>
              <p className="font-bold text-white text-sm">
                🔔 {newOrderIds.length} nouvelle{newOrderIds.length > 1 ? "s" : ""} commande{newOrderIds.length > 1 ? "s" : ""} !
              </p>
              <p className="text-xs text-white/80">Acceptez ou refusez chaque commande</p>
            </div>
          </div>
          <button
            onClick={acknowledge}
            className="flex items-center gap-1.5 rounded-xl bg-white px-4 py-2 text-sm font-bold text-brand-500 hover:bg-brand-50 flex-shrink-0"
          >
            <MdVolumeUp className="h-4 w-4" />
            Couper le son
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
          <IoFastFood className="h-7 w-7 text-brand-500" />
          Commandes
        </h2>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            placeholder="Référence..."
            className="rounded-xl border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 px-3 py-2 text-sm text-navy-700 dark:text-white w-32"
            value={searchRef}
            onChange={(e) => setSearchRef(e.target.value)}
          />
          <input
            type="date"
            className="rounded-xl border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 px-3 py-2 text-sm text-navy-700 dark:text-white"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
          <button
            onClick={() => load()}
            className="flex items-center gap-1.5 rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
          >
            <MdRefresh className="h-4 w-4" />
            Actualiser
          </button>
        </div>
      </div>

      <OperationalStatusBar />

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-4">
          {STATUS_GROUPS.map((group) => {
            const groupOrders = filtered.filter((o) => group.ids.includes(o.status_id));
            return (
              <div key={group.label}>
                <div className={`mb-3 flex items-center justify-between border-l-4 pl-3 ${group.border}`}>
                  <h3 className="font-bold text-navy-700 dark:text-white text-sm">{group.label}</h3>
                  <span className="rounded-full bg-gray-100 dark:bg-navy-700 px-2 py-0.5 text-xs font-semibold text-gray-600 dark:text-white">
                    {groupOrders.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {groupOrders.length === 0 ? (
                    <p className="text-center text-xs text-gray-400 py-8">Aucune commande</p>
                  ) : (
                    groupOrders.map((order) => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        onPrepare={handlePrepare}
                        onReady={handleReady}
                        onPrint={handlePrint}
                        onSelect={(o) => {
                          setDetailId(o.id);
                          if (o.status_id === 1) acknowledge();
                        }}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full detail modal */}
      {detailId && !acceptOrder && !refuseOrder && (
        <OrderDetailModal
          orderId={detailId}
          restaurantId={restaurantId}
          restaurant={restaurant}
          onClose={() => setDetailId(null)}
          onAccept={(o) => { setDetailId(null); setAcceptOrder(o); }}
          onRefuse={(o) => { setDetailId(null); setRefuseOrder(o); }}
          onPrepare={(id) => { handlePrepare(id); setDetailId(null); }}
          onReady={handleReady}
          onOrderUpdated={() => load(true)}
        />
      )}

      {acceptOrder && (
        <AcceptModal order={acceptOrder} onConfirm={handleAccept} onClose={() => setAcceptOrder(null)} />
      )}
      {refuseOrder && (
        <RefuseModal order={refuseOrder} onConfirm={handleRefuse} onClose={() => setRefuseOrder(null)} />
      )}
    </div>
  );
}
